import React, { useRef, useState } from 'react';

import { Alert, Button, Dialog, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';

import { type Reading, label } from './liveModel';
import { printableReport } from './printReport';

type SavedView = { name: string; search: string };
const storageKey = 'contribution-margin-views-v1';
function readViews(): Array<SavedView> {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
    return Array.isArray(stored)
      ? stored.filter((view) => typeof view?.name === 'string' && typeof view?.search === 'string')
      : [];
  } catch {
    return [];
  }
}

type Props = {
  ready: boolean;
  rows: Array<Reading>;
  title: string;
  source: string;
  range: string;
  search: string;
  onRestore: (search: string) => void;
};
export default function FinancialActions({ ready, rows, title, source, range, search, onRestore }: Props) {
  const latest = useRef({ ready, search });
  latest.current = { ready, search };
  const assertCurrent = () => {
    if (!latest.current.ready || latest.current.search !== search) {
      throw new Error('Complete current-scope data is required for export.');
    }
  };
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [views, setViews] = useState(readViews);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const save = () => {
    try {
      const next = [...views.filter((view) => view.name !== name.trim()), { name: name.trim(), search }];
      localStorage.setItem(storageKey, JSON.stringify(next));
      setViews(next);
      setMessage('View saved in this browser.');
    } catch {
      setMessage('This browser could not save the view.');
    }
  };
  const excel = async () => {
    setBusy(true);
    try {
      assertCurrent();
      const { Workbook } = await import('exceljs');
      assertCurrent();
      const workbook = new Workbook();
      const sheet = workbook.addWorksheet('Financial data', { views: [{ state: 'frozen', ySplit: 1 }] });
      const keys = [...new Set(rows.flatMap((row) => Object.keys(row)))];
      sheet.addRow(keys.map(label));
      for (const row of rows) {
        sheet.addRow(keys.map((key) => row[key] ?? 'Unknown'));
      }
      sheet.getRow(1).eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F464F' } };
        cell.font = { name: 'Arial', bold: true, color: { argb: 'FFFFFFFF' } };
      });
      sheet.columns.forEach((column) => {
        column.width = 24;
      });
      sheet.eachRow((row, index) => {
        if (index === 1) {
          return;
        }
        row.eachCell((cell) => {
          cell.font = { name: 'Arial' };
          if (typeof cell.value === 'number') {
            cell.numFmt = '#,##0.00;[Red]-#,##0.00';
            cell.alignment = { horizontal: 'right' };
          }
        });
      });
      const sources = workbook.addWorksheet('Sources');
      for (const row of [
        ['Report', title],
        ['Source', source],
        ['Period', range],
        ['Generated', new Date().toISOString()],
        ['View', search],
        ['Scope', 'All returned measures and rows, including selected comparison. Unknown is not zero.'],
      ]) {
        sources.addRow(row);
      }
      sources.columns.forEach((column) => {
        column.width = 55;
      });
      sources.eachRow((row) =>
        row.eachCell((cell) => {
          cell.font = { name: 'Arial' };
        }),
      );
      const bytes = await workbook.xlsx.writeBuffer();
      assertCurrent();
      const url = URL.createObjectURL(
        new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = 'contribution-margin.xlsx';
      link.click();
      URL.revokeObjectURL(url);
      setMessage('Excel export created.');
    } catch {
      setMessage('Excel export failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)}>Save, share and export</Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save and export this view</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            <TextField
              label="View name · saved in this browser"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Button disabled={!name.trim()} onClick={save}>
              Save view
            </Button>
            {views.length > 0 && (
              <TextField
                select
                label="Restore saved view"
                value=""
                onChange={(event) => {
                  onRestore(views[Number(event.target.value)].search);
                  setOpen(false);
                }}
              >
                {views.map((view, index) => (
                  <MenuItem key={view.name} value={index}>
                    {view.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  setMessage('View link copied. Recipients need existing report access.');
                } catch {
                  setMessage('Link could not be copied. Copy the address from your browser.');
                }
              }}
            >
              Copy view link
            </Button>
            <Button
              disabled={!ready || busy || !rows.length}
              onClick={() => {
                const popup = window.open('', '_blank');
                if (!popup) {
                  setMessage('Allow the print window to open, then try again.');
                  return;
                }
                popup.opener = null;
                popup.document.write(printableReport(title, source, range, rows));
                popup.document.close();
                popup.focus();
                popup.print();
              }}
            >
              Print all measures / PDF
            </Button>
            <Button disabled={!ready || busy || !rows.length} onClick={excel}>
              {busy ? 'Creating Excel…' : 'Export all measures Excel'}
            </Button>
            {message && <Alert severity="info">{message}</Alert>}
            <Button onClick={() => setOpen(false)}>Close</Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
